import {groupByUniqueKey} from '@benchling/js-utils';

import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import {ModifiedNucleotides} from 'biojs/Sequence';
import {ModifiedNucleotideAttributes, SequenceType} from 'constants/SequenceConstants';
import {MonomerNaturalAnalogue} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getNaturalMonomerId, nucleotideIsNatural} from 'molbio/monomers/MonomersUtil';
import {matchCase} from 'util/CapitalizationUtil';

export function containsMixedBases(mods: ModifiedNucleotides, nucleotideType: NucleotideType): boolean {
  const flexibleIds =
    nucleotideType === NucleotideType.DNA
      ? new Set(BenchlingConfig.MODBIO_URACIL_MONOMER_IDS)
      : new Set(BenchlingConfig.MODBIO_THYMINE_MONOMER_IDS);
  return mods.some((mod) => flexibleIds.has(mod.baseMonomerId));
}

export function getUTFlexibleNaturalBasesIfEnabled(
  bases: string,
  mods: ModifiedNucleotides,
  nucleotideType: NucleotideType,
  sequenceType: SequenceType
): string {
  if (!isFullUTFlexibilityEnabled() || sequenceType !== SequenceType.OLIGO) {
    return bases;
  }

  const flexibleIds =
    nucleotideType === NucleotideType.DNA
      ? new Set(BenchlingConfig.MODBIO_URACIL_MONOMER_IDS)
      : new Set(BenchlingConfig.MODBIO_THYMINE_MONOMER_IDS);
  const matchingPositions = new Set(
    mods.filter((mod) => flexibleIds.has(mod.baseMonomerId)).map((mod) => mod.position)
  );

  if (matchingPositions.size === 0) {
    return bases;
  }

  const flexibleBase =
    nucleotideType === NucleotideType.DNA ? MonomerNaturalAnalogue.U : MonomerNaturalAnalogue.T;
  return bases
    .split('')
    .map((base, i) => (matchingPositions.has(i) ? matchCase(base, flexibleBase) : base))
    .join('');
}

export function isFullUTFlexibilityEnabled(): boolean {
  return BenchlingConfig.MOLBIO_ENABLE_UT_FLEXIBILITY;
}

export function getModsForUTFlexibleNaturalBasesIfEnabled(
  bases: string,
  targetNucleotideType: NucleotideType,
  targetSequenceType: SequenceType
): ModifiedNucleotides {
  if (!isFullUTFlexibilityEnabled() || targetSequenceType !== SequenceType.OLIGO) {
    return [];
  }

  const incompatibleBase =
    targetNucleotideType === NucleotideType.DNA ? MonomerNaturalAnalogue.U : MonomerNaturalAnalogue.T;
  const baseMonomerId = getNaturalMonomerId(incompatibleBase);
  const sugarMonomerId = getNaturalMonomerId(
    targetNucleotideType === NucleotideType.DNA
      ? MonomerNaturalAnalogue.D_LOWER
      : MonomerNaturalAnalogue.R_LOWER
  );
  const phosphateMonomerId = getNaturalMonomerId(MonomerNaturalAnalogue.P_LOWER);

  const mods: Array<ModifiedNucleotideAttributes> = [];
  Array.from(bases.toUpperCase()).forEach((base, i) => {
    if (base === incompatibleBase) {
      mods.push({
        position: i,
        baseMonomerId,
        sugarMonomerId,
        phosphateMonomerId,
      });
    }
  });

  return mods;
}

export function lockImplicitMods(
  bases: string,
  modifiedNucleotides: ModifiedNucleotides,
  sourceNucleotideType: NucleotideType,
  targetNucleotideType: NucleotideType
): ModifiedNucleotides {
  const sugarMonomerId = getNaturalMonomerId(
    sourceNucleotideType === NucleotideType.DNA
      ? MonomerNaturalAnalogue.D_LOWER
      : MonomerNaturalAnalogue.R_LOWER
  );
  const phosphateMonomerId = getNaturalMonomerId(MonomerNaturalAnalogue.P_LOWER);

  const existingModsByPos = groupByUniqueKey(
    modifiedNucleotides as Array<ModifiedNucleotideAttributes>,
    (mod) => mod.position
  );
  const newMods: Array<ModifiedNucleotideAttributes> = [];
  Array.from(bases.toUpperCase()).forEach((base, i) => {
    const existingMod = existingModsByPos[i];
    if (existingMod) {
      newMods.push(existingMod);
    } else {
      newMods.push({
        position: i,
        baseMonomerId: getNaturalMonomerId(base.toUpperCase() as MonomerNaturalAnalogue),
        sugarMonomerId,
        phosphateMonomerId,
      });
    }
  });

  return newMods.filter((mod) => !nucleotideIsNatural(mod, targetNucleotideType));
}

export function reindexModifiedNucleotides(
  mods: Readonly<Array<ModifiedNucleotideAttributes>>,
  start: number
): Readonly<Array<ModifiedNucleotideAttributes>> {
  return mods.map((mod) => ({
    ...mod,
    position: mod.position + start,
  }));
}
